<template>
	<PageLoading
		:loading="!user.uuid || user.loadingProfile"
		class="relative mt-4"
	>
		<div class="flex items-center justify-between gap-5 px-4">
			<PageTitle class="!px-0">{{ user.name }}</PageTitle>
			<LevelPill>{{ t('level', [user.level]) }}</LevelPill>
		</div>
		<PagePadding class="mt-5">
			<ProgressBar
				color="secondary"
				class="mt-4"
				:value="user.levelPercent / 100"
			/>
			<div
				class="text-label-tertiary mt-3 flex items-center justify-between gap-2 text-sm font-medium capitalize"
			>
				<div>{{ t('progress') }}</div>
				<div>{{ xp }}/{{ xpLevel }} XP</div>
			</div>
			<BaseCard class="mt-9" v-if="user.bestUnboxing">
				<div
					class="relative flex items-center justify-between gap-4 py-5 pr-5 pl-30"
				>
					<LazySticker
						class="!absolute -top-3 left-5 h-21 w-21"
						:src="animation(user.modelUuid)"
						:preloader="user.preloader"
						:loop="false"
						:autoplay="true"
					/>
					<div class="min-w-0">
						<div
							class="text-label-secondary min-w-0 truncate text-xs font-medium uppercase"
						>
							{{ user.collectionName }}
						</div>
						<div
							class="text-label-primary mt-1 min-w-0 truncate text-sm font-medium capitalize"
						>
							{{ user.modelName }}
						</div>
					</div>
					<CoinPrice :price="user.price" />
				</div>
				<div
					class="flex items-center justify-between gap-4 border-t border-black py-3 pr-4 pl-5"
				>
					<div class="text-label-secondary text-xs font-medium uppercase">
						{{ t('bestUnboxing') }}
					</div>
					<RouterLink
						:to="`/case/${user.caseUuid}`"
						v-slot="{ navigate }"
						custom
					>
						<div
							class="inline-flex items-center gap-2"
							v-hover="{ active: 'scale-95', impact: 'soft' }"
							@click="navigate"
						>
							<LazyImage
								class="h-8 w-8"
								:src="caseIcon(user.caseUuid, true)"
								:alt="user.caseName"
							/>
							<div class="text-label-tertiary text-sm font-medium capitalize">
								{{ user.caseName }}
							</div>
						</div>
					</RouterLink>
				</div>
			</BaseCard>
			<SubtitleText class="mt-6">{{ t('stats') }}</SubtitleText>
			<div class="mt-2 flex gap-1.5">
				<ProfileStat :label="t('battlesPlayed')" :value="user.battlesPlayed">
					<SwordIcon />
				</ProfileStat>
				<ProfileStat :label="t('casesOpened')" :value="user.casesOpened">
					<BoxIcon />
				</ProfileStat>
			</div>
			<div class="mt-9 flex gap-5">
				<RouterLink to="/profile" custom v-slot="{ navigate, isExactActive }">
					<div
						@click="navigate"
						class="font-squad text-label-primary text-2xl font-black uppercase"
						:class="{
							'opacity-30': !isExactActive
						}"
						v-hover="{ active: 'scale-95' }"
					>
						{{ t('fairness') }}
					</div>
				</RouterLink>
				<RouterLink
					to="/profile/transactions"
					custom
					v-slot="{ navigate, isExactActive }"
				>
					<div
						@click="navigate"
						class="font-squad text-label-primary text-2xl font-black uppercase"
						:class="{
							'opacity-30': !isExactActive
						}"
						v-hover="{ active: 'scale-95' }"
					>
						{{ t('transactions') }}
					</div>
				</RouterLink>
			</div>
		</PagePadding>
		<RouterView class="mt-4" />
	</PageLoading>
</template>
<script setup lang="ts">
import PageTitle from '@/components/PageTitle.vue'
import PagePadding from '@/components/PagePadding.vue'
import SubtitleText from '@/components/SubtitleText.vue'
import LazyImage from '@/components/LazyImage.vue'
import { computed, ref, watch } from 'vue'
import ProgressBar from '@/components/ProgressBar.vue'
import BaseCard from '@/components/BaseCard.vue'
import { useLayout } from '@/stores/layout'
import { useUser } from '@/stores/user'
import PageLoading from '@/components/PageLoading.vue'
import { animation, caseIcon } from '@/utils/urls'
import LevelPill from '@/components/LevelPill.vue'
import CoinPrice from '@/components/CoinPrice.vue'
import SwordIcon from '@/assets/icons/sword.svg'
import BoxIcon from '@/assets/icons/box.svg'
import ProfileStat from '@/components/ProfileStat.vue'
import LazySticker from '@/components/LazySticker.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n({
	messages: {
		en: {
			level: 'Level {0}',
			progress: 'Progress',
			bestUnboxing: 'Best Unboxing',
			stats: 'Stats',
			fairness: 'Fairness',
			transactions: 'Transactions',
			battlesPlayed: 'Battles Played',
			casesOpened: 'Cases Opened'
		},
		ru: {
			level: 'Уровень {0}',
			progress: 'Прогресс',
			bestUnboxing: 'Лучший дроп',
			stats: 'Статистика',
			fairness: 'Честность',
			transactions: 'Транзакции',
			battlesPlayed: 'Батлов сыграно',
			casesOpened: 'Кейсов открыто'
		}
	}
})

const layout = useLayout()

layout.showBottomNavbar()

const user = useUser()

const cases = ref<
	{
		uuid: string
		name: string
		risk: number
		price: number
	}[]
>()

const formatNumberWithSpaces = (num: number): string => {
	return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

const xp = computed(() => {
	return formatNumberWithSpaces(user.xp)
})

const xpLevel = computed(() => {
	return formatNumberWithSpaces(user.xpLevel)
})

watch(
	() => user.uuid,
	() => {
		if (!user.uuid) {
			return
		}

		user.getProfile()
	},
	{ immediate: true }
)
</script>
